/**
 * PLOT PLANNER — Dynasty Map colour + allegiance resolution (pure, genre-neutral)
 * ──────────────────────────────────────────────────────────────────────────────
 * Replaces the book-specific leak (fixed Mystic/Old/Military/Trade allegiance
 * shapes + an index-based colour palette) with:
 *
 *   • per-house colour from a STABLE name hash → HSL — order-independent, no
 *     palette ceiling, same name always the same colour; and
 *   • allegiance resolved LIVE from the author's own factions by id → name, so a
 *     rename propagates and a deleted faction (or a legacy shape-enum value)
 *     resolves to nothing rather than dangling.
 *
 * No Firebase / React deps, so it's unit-tested directly. See
 * docs/dynasty-genre-neutral-sketch.md.
 */

const FALLBACK_COLOUR = '#c9bfa6'; // blank / unaffiliated house

// Deterministic hue [0,360) from a house name. Order-independent and unbounded —
// adding or removing a house never reshuffles the others' colours.
export function hashHue(str) {
  const s = String(str || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

// HSL → #rrggbb so the existing txtOn() (luminance-based readable text) keeps working.
export function hslToHex(h, s, l) {
  const sf = s / 100;
  const lf = l / 100;
  const a = sf * Math.min(lf, 1 - lf);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = lf - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Auto colour for a house name — muted + on the darker side so it reads on the
// parchment theme and carries readable (usually white) text.
export function autoHouseColour(name) {
  const n = String(name || '').trim();
  if (!n) return FALLBACK_COLOUR;
  return hslToHex(hashHue(n), 48, 40);
}

// Colour for a house: an author override in dynasty.houseColors wins, else auto.
// Structured this way so a per-house colour picker is a trivial later add — only
// the write side (a mutator + picker UI) is missing.
export function resolveHouseColour(dynasty, name) {
  const n = String(name || '').trim();
  if (!n) return FALLBACK_COLOUR;
  const override = dynasty && dynasty.houseColors && dynasty.houseColors[n];
  return override || autoHouseColour(n);
}

// The faction a house is allied to, resolved live: allegiance stores the faction
// id; we look up the CURRENT name. Returns '' when unset, when the faction was
// deleted (dangling id), or for legacy shape-enum values (which match no id) —
// i.e. graceful-ignore with no dangling display.
export function resolveAllegianceName(factions, allegiance, house) {
  const id = allegiance && allegiance[house];
  if (!id) return '';
  const f = (Array.isArray(factions) ? factions : []).find((x) => x && x.id === id);
  return f ? String(f.name || '').trim() : '';
}
